import type { Permissao } from '@wms/shared';

export type Status =
  | 'capturada'
  | 'aguardando_xml'
  | 'sem_oc'
  | 'divergente'
  | 'recusada'
  | 'validada'
  | 'aguardando_agendamento'
  | 'agendada'
  | 'aguardando_transferencia'
  | 'em_transito_full'
  | 'em_conferencia'
  | 'divergencia_recebimento'
  | 'estocada'
  | 'disponivel_venda'
  | 'cancelada'
  | 'fora_do_fluxo';

export type Tom = 'neutro' | 'marca' | 'ok' | 'alerta' | 'erro';

export interface Etapa {
  status: Status;
  rotulo: string;
  explica: string;
  dono: 'comprador' | 'fiscal' | 'logistica' | 'cd' | null;
  permissao: Permissao | null;
  tom: Tom;
  final?: boolean;
}

export interface Contagem {
  status: Status;
  total: number;
  parados: number;
  maisAntigo: string | null;
}

export interface Nota {
  chave: string;
  status: Status;
  statusDesde: string;
  empresa: number | null;
  emitenteCnpj: string;
  emitenteNome: string | null;
  emitenteUf?: string | null;
  numero: number | null;
  serie: string | null;
  emissao: string | null;
  valor: number | null;
  natOp: string | null;
  infCpl: string | null;
  resumo: boolean;
  pedidoCompra: number | null;
  ocOrigem: string | null;
  comprador: number | null;
  compradorNome: string | null;
  nfCompra: number | null;
  nfCompraDestino: number | null;
  recebimento: number | null;
  destinoId: number | null;
  destinoNome: string | null;
  agendaData: string | null;
  agendaJanela: string | null;
  dohFuturo: number | null;
  motivo: string | null;
  slackThread: string | null;
  divergenciasAbertas: number;
  divergenciasResumo?: ResumoDivergencia[];
  /** Última mensagem no Slack: RECEBIDA = alguém respondeu e ninguém falou depois. */
  conversaUltima?: { sentido: 'ENVIADA' | 'RECEBIDA'; quando: string; autor: string | null } | null;
  /** Entrou com divergência e já foi aprovada: a última aprovação. Sem divergência aberta, saiu da cobrança. */
  aprovacao?: { por: string | null; quando: string; motivo: string } | null;
  /** Só em "A agendar": para onde tende a ir, pelo centro dos itens na entrada fiscal. */
  preDestino?: { tipo: 'fica' | 'full' | 'consumo' | 'misto'; texto: string; destinoId: number | null } | null;
  /** Só no Recebimento (com cega): onde a cega está entre contar e guardar. */
  recebimentoProgresso?: ProgressoRecebimento | null;
  /** Concluída depois de chegar com contagem diferente da nota. */
  chegouDivergente?: boolean;
  /** A mercadoria já saiu por NF de transferência feita no Procfit (antes do WMS transferir). */
  jaTransferida?: JaTransferida | null;
  /** Antes da transferência: a NF de compra foi importada sem validação no Procfit (sem estoque); o fiscal precisa salvá-la. */
  compraSemValidacao?: boolean;
}

export interface JaTransferida {
  nf: number;
  data: string;
  entidade: number | null;
  centroDestino: number | null;
  nfCompraDestino: number | null;
  empresaDestino: number | null;
}

/** A frase da transferência já feita no Procfit, igual à da API. */
export function textoJaTransferida(j: JaTransferida): string {
  const quando = j.data.split('-').reverse().join('/');
  return j.centroDestino
    ? `Já transferida pela NF ${j.nf} em ${quando}; entrou na empresa ${j.empresaDestino ?? '?'} (centro ${j.centroDestino}).`
    : `Já transferida pela NF ${j.nf} em ${quando}; ainda sem entrada no destino.`;
}

/** Onde a cega está: contagem → aprovação → etiqueta → alocação. */
export type FaseRecebimento = 'contagem' | 'aprovacao' | 'etiqueta' | 'alocar' | 'parcial' | 'alocada';

export interface ProgressoRecebimento {
  recebimento: number;
  fase: FaseRecebimento;
  conferido: boolean;
  aprovado: boolean;
  produtosSemEtiqueta: number;
  etiquetas: number;
  alocadas: number;
  unidades: number;
  unidadesAlocadas: number;
  /** Notas da esteira na mesma cega. */
  notasNaCega: number;
}

/** Uma fase da cega no card: conta cega (o trabalho no chão), não nota. */
export interface Subetapa {
  cegas: number;
  notas: number;
  /** Cegas com nota há +24 h em conferência. */
  paradas: number;
}

/** "Em conferência" aberta no card do Recebimento, na ordem do chão. */
export const SUBETAPAS_RECEBIMENTO: { fase: FaseRecebimento; rotulo: string; explica: string }[] = [
  { fase: 'contagem', rotulo: 'Aguardando contagem', explica: 'Cega gerada; o coletor ainda não contou.' },
  { fase: 'aprovacao', rotulo: 'Contada, falta aprovar', explica: 'O coletor contou; falta aprovar o recebimento.' },
  { fase: 'etiqueta', rotulo: 'Falta etiqueta', explica: 'Aprovada; falta gerar as etiquetas de alocação.' },
  { fase: 'alocar', rotulo: 'Etiquetada, nada alocado', explica: 'Etiquetas prontas; nenhum volume guardado ainda.' },
  { fase: 'parcial', rotulo: 'Alocado parcialmente', explica: 'Parte dos volumes já foi guardada; o resto (ou produto sem etiqueta) ainda não.' },
  { fase: 'alocada', rotulo: 'Tudo alocado', explica: 'Todos os volumes guardados; a nota vira Estocada na próxima rodada.' },
];

/** O que falta, numa frase, para a linha da esteira. */
export function textoProgresso(p: ProgressoRecebimento): string {
  const cega = `Cega ${p.recebimento}${p.notasNaCega > 1 ? ` (${p.notasNaCega} NFs)` : ''}`;
  switch (p.fase) {
    case 'contagem':
      return `${cega}: aguardando contagem`;
    case 'aprovacao':
      return `${cega}: contada, falta aprovar`;
    case 'etiqueta':
      return `${cega}: ${p.produtosSemEtiqueta || 'todo'} produto(s) sem etiqueta`;
    case 'alocar':
      return `${cega}: 0 de ${p.etiquetas} volume(s) alocado(s)`;
    case 'parcial':
      return `${cega}: ${p.alocadas} de ${p.etiquetas} volume(s) alocado(s), faltam ${p.etiquetas - p.alocadas}${p.produtosSemEtiqueta ? ` + ${p.produtosSemEtiqueta} produto(s) sem etiqueta` : ''}`;
    case 'alocada':
      return `${cega}: tudo alocado`;
  }
}

export interface Divergencia {
  id: number;
  nItem: number | null;
  regra: string;
  esperado: string | null;
  veio: string | null;
  mensagem: string;
  /** Item fora da OC: outra OC aberta do mesmo fornecedor que tem o SKU. */
  ocSugerida?: number | null;
}

export interface Item {
  nItem: number;
  cprod: string | null;
  descricao: string | null;
  quantidade: number | null;
  unidade: string | null;
  valorUnit: number | null;
  valorTotal: number | null;
  xPed: string | null;
  produto: number | null;
  ean?: string | null;
  /** Nome do SKU no ERP (para o "código da nota → SKU" do roteiro). */
  descricaoErp?: string | null;
  /** EAN do SKU no cadastro (o que bate com o da nota, se houver). */
  eanErp?: string | null;
  /** Todos os EANs do SKU no cadastro do PBS (PRODUTOS_EAN). */
  eansErp?: string[];
}

export interface Evento {
  quando: string;
  tipo: 'status' | 'nota' | 'slack';
  de: Status | null;
  para: Status | null;
  autor: string;
  texto: string | null;
  marca?: 'apagada' | 'editada' | null;
  lado?: 'enviada' | 'recebida' | null;
}

export interface Sugestao {
  nfCompra: number;
  numero: number | null;
  serie: string | null;
  data: string | null;
  itensEmComum: number;
  itensDaNota: number;
  quantidadeBate: boolean;
}

export interface DetalheNota {
  nota: Nota;
  etapa: Etapa;
  itens: Item[];
  divergencias: Divergencia[];
  linhaDoTempo: Evento[];
  slackLink: string | null;
  podeAgir: boolean;
  /** Logística pode voltar Agendada/A transferir para A agendar. */
  podeDesagendar?: boolean;
  podeLiberarDoh: boolean;
  podeDispensar: boolean;
  podeReincluir: boolean;
  sugestoesTransferencia: Sugestao[];
  ocsDoFornecedor: OcDoFornecedor[];
  /** Itens cujo código do fornecedor não tem de-para no ERP (PRODUTOS_FORNECEDORES). */
  deParas?: DeParaPendente[];
  /** Pode confirmar e gravar o de-para (comprador, fiscal ou cadastro). */
  podeDePara?: boolean;
  /** false: o CNPJ do emitente não tem ENTIDADE no ERP (sem de-para nem OC até cadastrar). */
  fornecedorNoErp?: boolean;
  /** Último pedido de autorização no Slack desta nota. */
  autorizacaoPedida?: { quando: string; autor: string | null } | null;
  /** Sem OC: a última vez que perguntaram a OC no Slack. */
  ocPerguntada?: { quando: string; autor: string | null } | null;
  /** O pré-pedido de onde a OC da nota saiu (null = sem OC ou OC sem pré-pedido). */
  prePedido?: number | null;
  /** Slack de quem chamar no roteiro: o comprador da OC (ou da nota) e quem aprova DOH. */
  slackPessoas?: { comprador: string[]; aprovaDoh: string[] };
  /** Chegou divergente: o que a cega contou diferente da nota, só os produtos desta nota. */
  contagemCega?: { recebimento: number; itens: { produto: number; descricao: string | null; nota: number; contado: number }[] } | null;
  /** `entradas.cega_junto_entrada`: a cega sai sempre junto com a entrada. */
  cegaJunto: boolean;
}

export interface DeParaPendente {
  nItem: number;
  cprod: string;
  ean: string | null;
  descricao: string | null;
  quantidade: number | null;
  unidade: string | null;
  /** O SKU que o WMS achou pelo EAN: a sugestão da pergunta. */
  produto: number | null;
  descricaoProduto: string | null;
}

export interface OcDoFornecedor {
  oc: number;
  data: string;
  comprador: string | null;
  valor: number;
  itensOc: number;
  emComum: number;
  itensNota: number;
}

export interface Destino {
  id: number;
  operacao: string;
  nome: string;
  uf: string;
  tipo: 'ARMAZEM' | 'FULL';
  empresa: number;
  centroRecebimento: number | null;
  centroVenda: number;
  centroTransito: number | null;
  serieTransferencia: string | null;
  /** NF de transferência (migration 029). */
  centroSaidaTransf: number | null;
  operacaoSaidaTransf: number | null;
  operacaoEntradaTransf: number | null;
  ativo: boolean;
  observacao: string | null;
}

export interface Doh {
  limite: number;
  dohFuturo: number | null;
  travado: boolean;
  motivo: string | null;
  alerta: string | null;
  /** Há item acima do limite (a outra trava é produto sem histórico no destino). */
  acimaDoLimite?: boolean;
  /** Já transferida no Procfit; jaNoEstoque = a quantidade da nota já está no estoque do destino (não soma de novo). */
  jaTransferida?: (JaTransferida & { texto: string; jaNoEstoque: boolean }) | null;
  /** Onde foi medido (o destino do agendamento); null = grupo inteiro. */
  escopo: { destino: string; centros: number[]; centroVenda: number } | null;
  /** `entradas.doh_sem_historico_libera`: produto sem histórico não trava (só alerta). */
  semHistoricoLibera?: boolean;
  /** `entradas.doh_exige_aprovacao_slack`: acima do DOH só com aprovação no Slack. */
  exigeAprovacaoSlack?: boolean;
  /** Pedido de aprovação desta nota ainda sem decisão. */
  aprovacaoPendente?: { id: number; quando: string; solicitante: string } | null;
  itens: {
    produto: number;
    descricao: string | null;
    quantidadeNota: number;
    estoque: number;
    vendaDia: number | null;
    dohHoje: number | null;
    dohFuturo: number | null;
    acima: boolean;
    /** De onde saiu o ritmo: últimos 90 dias, a janela antes da última venda, ou nada. */
    base: 'atual' | 'historico' | 'nunca_vendeu';
    baseDe: string | null;
    baseAte: string | null;
    cadastro: string | null;
  }[];
}

export interface RespostaEsteira {
  etapas: Etapa[];
  contagens: Contagem[];
  /** "Em conferência" por fase da cega. */
  subetapas?: Partial<Record<FaseRecebimento, Subetapa>>;
  notas: Nota[];
  /** Por OC da lista: valor da OC × soma de TODAS as NFs dela na esteira (não só as filtradas). */
  ocs?: Record<number, ValorOc>;
  slack: boolean;
}

export interface ValorOc {
  valorOc: number;
  valorNotas: number;
  notas: number;
  /** A soma das NFs passa do valor da OC. */
  acima: boolean;
  /** Último aviso ao comprador no Slack. */
  avisado: { quando: string; autor: string | null } | null;
  /** OC × pré-pedido aprovado; null = a OC não saiu de pré-pedido. */
  prePedido: OcPrePedido | null;
  /** A diferença da OC já foi aprovada (por quem aprova DOH): não cobra de novo. Só vem em /api/entradas/ocs. */
  autorizada?: boolean;
}

/** fora: SKU na OC que o pré-pedido não tem · removido: do pré-pedido, não está mais na OC. */
export type TipoOcPre = 'fora' | 'removido' | 'quantidade' | 'valor';

export interface OcPrePedido {
  numero: number;
  valorPre: number;
  itens: { produto: number; descricao: string | null; tipo: TipoOcPre; qtdOc: number | null; qtdPre: number | null; valorOc: number | null; valorPre: number | null }[];
}

/** As etapas em grupos, na ordem do fluxo — é como a esteira se lê. */
export const FASES: { titulo: string; status: Status[] }[] = [
  { titulo: 'Conferência com a OC', status: ['capturada', 'aguardando_xml', 'sem_oc', 'divergente', 'recusada', 'validada'] },
  { titulo: 'Agendamento', status: ['aguardando_agendamento', 'aguardando_transferencia', 'agendada', 'em_transito_full'] },
  { titulo: 'Recebimento', status: ['em_conferencia', 'divergencia_recebimento'] },
  { titulo: 'Concluídas (7 dias)', status: ['estocada', 'disponivel_venda', 'cancelada', 'fora_do_fluxo'] },
];

export const moeda = (v: number | null | undefined): string =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** "há 3 h", "há 2 dias" — o tempo parado é o que ordena a fila. */
export function haQuanto(iso: string | null): string {
  if (!iso) return '';
  const min = Math.max(0, (Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 60) return `há ${Math.round(min)} min`;
  if (min < 48 * 60) return `há ${Math.round(min / 60)} h`;
  return `há ${Math.round(min / 1440)} dias`;
}

// ------------------------------------------------------------------ NF de transferência

export interface ItemTransferencia {
  nfCompra: number;
  numeroCompra: number | null;
  chaveCompra: string | null;
  nfCompraProduto: number;
  produto: number;
  descricao: string | null;
  unidade: string | null;
  quantidadeNota: number;
  saldo: number;
  quantidade: number;
  centroCompra: number;
  /** Estoque do ERP no centro de saída (já contando o que chega com a compra). */
  estoqueSaida: number | null;
  valorCompra: number | null;
  ipiUnitario: number;
  valorUnitario: number | null;
  total: number;
  cfop: string | null;
  cst: string | null;
  aliquota: number | null;
  icmsValor: number;
  classifFiscal: string | null;
  grupo: number | null;
  problema: string | null;
}

export interface PreviaTransferencia {
  nfCompra: number;
  numeroCompra: number | null;
  empresaOrigem: number;
  ufOrigem: string | null;
  destino: { id: number; nome: string; uf: string; empresa: number; tipo: 'ARMAZEM' | 'FULL'; fantasia: string | null; centro: number | null; centroNome: string | null };
  origem: { fantasia: string | null; centroNome: string | null };
  ufDestino: string | null;
  centroSaida: number;
  operacao: number;
  operacaoEntrada: number;
  serie: string;
  valorBase: 'compra_ipi' | 'compra';
  notaInteira: boolean;
  itens: ItemTransferencia[];
  total: number;
  icmsTotal: number;
  pesoTotal: number;
  bloqueios: string[];
  avisos: string[];
}

export interface RastroTransferencia {
  id: number;
  status: 'EMITIDA' | 'AUTORIZADA' | 'ENTRADA' | 'DIVERGENTE' | 'ERRO' | 'CANCELADA';
  nfFaturamento: number | null;
  numero: number | null;
  serie: string | null;
  chaveNfe: string | null;
  statusNfe: string | null;
  destino: number;
  centroSaida: number;
  operacao: number;
  notaInteira: boolean;
  total: number;
  itens: { produto: number; quantidade: number; valorUnitario: number; cst: string | null; aliquota: number | null; icms: number }[];
  criadoPor: string;
  criadoEm: string;
  autorizadaEm: string | null;
  entrada: { nfCompra: number; empresa: number; numero: number | null; centro: number | null; quando: string | null } | null;
  entradaEm: string | null;
  erro: string | null;
}

export interface LinhaTributacao {
  empresa: number;
  operacao: number;
  ufOrigem: string | null;
  ufDestino: string;
  grupo: number | null;
  descricao: string | null;
  cst: string | null;
  aliquota: number | null;
  ultimaCst: string | null;
  ultimaAliquota: number | null;
  ultimaNf: number | null;
  ultimaEmissao: string | null;
  produtos: number;
  itens: number;
}

// ------------------------------------------------------------------ Montar carga

export interface NotaParaCarga {
  chave: string;
  nfCompra: number;
  numero: number | null;
  emitente: string | null;
  empresa: number;
  status: Status;
  emissao: string | null;
  itens: number;
  unidades: number;
  saldo: number;
  valor: number;
  produtos: { produto: number; descricao: string | null; saldo: number }[];
  emOutrasCargas: number;
  /** Saldo da NF limitado pelo estoque do ERP no centro de saída. */
  disponivel: number;
}

export interface ItemDaCarga {
  nfCompra: number;
  numeroCompra: number | null;
  chave: string | null;
  nfCompraProduto: number;
  produto: number;
  descricao: string | null;
  quantidadeNota: number;
  saldo: number;
  valorUnitario: number | null;
  pesoUnitario: number | null;
  emOutrasCargas: { carga: number; quantidade: number }[];
  centroSaida: number;
  disponivel: number;
  motivoEstoque: string | null;
  /** Na tela: o saldo da NF (o campo `saldo` passa a ser o disponível). */
  saldoNf?: number;
}

export interface PlanoCarga {
  compras: number[];
  valorBase?: 'compra_ipi' | 'compra' | null;
  destinos: { destinoId: number; linhas: { nfCompraProduto: number; quantidade: number }[] }[];
}

export interface PreviaCarga {
  destinos: (PreviaTransferencia & { emitida: { numero: number | null; status: string } | null })[];
  excedidos: { nfCompraProduto: number; produto: number; saldo: number; distribuido: number }[];
  sobra: number;
  conflitos: { produto: number; saldo: number; aqui: number; outras: number; cargas: number[] }[];
  estoque: string[];
  ligada: boolean;
  podeEmitir: boolean;
}

export interface ResumoCarga {
  id: number;
  nome: string;
  status: 'RASCUNHO' | 'EMITINDO' | 'CONCLUIDA' | 'CANCELADA';
  criadoPor: string;
  criadoEm: string;
  atualizadoEm: string | null;
  atualizadoPor: string | null;
  plano: PlanoCarga;
  nfs: { destino: number; numero: number | null; status: string }[];
}

export interface ResumoDivergencia {
  regra: string;
  nItem: number | null;
  esperado: string | null;
  veio: string | null;
  mensagem?: string | null;
  cprod: string | null;
  /** O SKU no ERP (achado pelo EAN ou pela referência do fornecedor). */
  produto?: number | null;
  /** Item fora da OC: outra OC aberta do mesmo fornecedor que tem o SKU. */
  ocSugerida?: number | null;
  descricao: string | null;
  quantidade: number | null;
  valorUnit: number | null;
}

/**
 * Os preços da mensagem de preço: Nota, Pré-pedido (o aprovado) e OC hoje.
 * Lê o formato novo ("Nota R$ X/un · Pré-pedido N R$ Y/un · OC N hoje R$ Z/un")
 * e o antigo ("R$ X por unidade, a OC N diz R$ Y"), que continua nas notas velhas.
 */
export function precosDaMensagem(msg: string): { nota: number; pre: number | null; prePedido: string | null; oc: number | null; ref: number } | null {
  const real = (v: string): number => Number(v.replace(/\./g, '').replace(',', '.'));
  const novo = /Nota R\$\s*([\d.,]+)\/un · (?:Pré-pedido (\d+) R\$\s*([\d.,]+)\/un · OC \d+ hoje R\$\s*([\d.,]+)\/un|OC \d+ R\$\s*([\d.,]+)\/un)/.exec(msg);
  if (novo) {
    const pre = novo[3] ? real(novo[3]) : null;
    const oc = novo[4] ? real(novo[4]) : novo[5] ? real(novo[5]) : null;
    return { nota: real(novo[1]!), pre, prePedido: novo[2] ?? null, oc, ref: Math.min(...[pre, oc].filter((x): x is number => x != null)) };
  }
  const velho = /R\$\s*([\d.,]+) por unidade, a OC \d+ diz R\$\s*([\d.,]+)/.exec(msg);
  return velho ? { nota: real(velho[1]!), pre: null, prePedido: null, oc: real(velho[2]!), ref: real(velho[2]!) } : null;
}

/** O nome curto de cada regra de divergência (lista e gaveta). */
export const TIPO_DIVERGENCIA: Record<string, string> = {
  // Mesmos nomes do aviso no Slack (formatos de 01/11/2026): tudo contra o pré-pedido aprovado.
  preco: 'Preço acima do aprovado',
  quantidade: 'Quantidade acima do aprovado',
  item_fora_oc: 'SKU sem aprovação (fora da OC)',
  fora_pre_pedido: 'SKU sem aprovação (fora do pré-pedido)',
  produto: 'Produto sem cadastro',
  oc: 'Sem OC',
  oc_fechada: 'OC encerrada',
  fornecedor: 'Fornecedor',
  destinatario: 'Empresa',
};
